import React, { useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatSpeakerLabel } from "./speakerColors";

interface NewEntryFormProps {
  speakers: string[];
  defaultSpeaker?: string;
  title?: string;
  onSubmit: (speaker: string, text: string) => void;
  onCancel: () => void;
}

/**
 * Collects a new entry's speaker and content BEFORE its time range is set.
 * Content is required — the "Set time range" step is disabled until text is
 * entered, so entries can never be created empty.
 */
export const NewEntryForm: React.FC<NewEntryFormProps> = ({
  speakers,
  defaultSpeaker,
  title = "New transcript entry",
  onSubmit,
  onCancel,
}) => {
  const [speaker, setSpeaker] = useState(defaultSpeaker ?? speakers[0] ?? "");
  const [text, setText] = useState("");

  const canContinue = !!speaker && text.trim().length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Stack spacing={1.5}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            Speaker
          </Typography>
          <Select
            size="small"
            value={speaker}
            onChange={(e) => setSpeaker(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            {speakers.map((s) => (
              <MenuItem key={s} value={s}>
                {formatSpeakerLabel(s)}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <TextField
          label="What was said"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          size="small"
          autoFocus
          placeholder="Enter the transcript text for this entry…"
        />
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowForwardIcon fontSize="small" />}
            onClick={() => onSubmit(speaker, text.trim())}
            disabled={!canContinue}
            sx={{ textTransform: "none" }}
          >
            Set time range
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={onCancel}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};
